import { pickBy, snakeCase } from 'lodash';
import React, { FC, useCallback, useState } from 'react';
import { Switch, Case } from 'react-if';
import { useApolloClient, useMutation } from '@apollo/client';

import { Title } from '@nerdwallet/currency/components/Title';
import { Text } from '@nerdwallet/currency/components/Text';
import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { Stack } from '@nerdwallet/currency/components/Stack';

import ErrorBanner from '@components/error-banner/error-banner';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import { useAnalytics } from '@lib/segment';
import { InsuranceType } from '@lib/types/insurance';

import AutoInfoForm from './components/auto-info-form';
import useForm from './components/form-builder/use-form';
import {
  ReviewInformationFormData,
  parseBirthdate,
  convertResponseToFormData,
  validators,
} from './utils';

import HomeInfoForm from './components/home-info-form';
import PersonalInfoForm from './components/personal-info-form';
import {
  INSURANCE_POLICY_OVERRIDES_QUERY,
  UPDATE_INSURANCE_POLICY_OVERRIDES_MUTATION,
} from '@lib/queries/insurance';
import RentersInfoForm from './components/renters-info-form';

const logger = getLogger('ReviewInformationForm');

type ReviewInformationFormProps = {
  carrierName: string;
  onSubmit?: () => void;
  policyId: string;
  policyType: string;
};

const ReviewInformationForm: FC<ReviewInformationFormProps> = ({
  carrierName,
  onSubmit,
  policyId,
  policyType,
}) => {
  const { segmentTrack } = useAnalytics();
  const apolloClient = useApolloClient();

  const [initialFormData, setInitialFormData] = useState({});

  const [Form, { formData, formErrors, setFormData }] =
    useForm<ReviewInformationFormData>({
      validators,
    });

  const hasValidationErrors = Object.keys(formErrors).length > 0;

  const {
    data,
    loading,
    error: getPolicyOverridesError,
  } = useQueryWithLog(logger, INSURANCE_POLICY_OVERRIDES_QUERY, {
    variables: {
      policyId,
    },
    onCompleted(data) {
      const convertedResponseToFormData = convertResponseToFormData(data);

      setInitialFormData(convertedResponseToFormData);
      setFormData(convertedResponseToFormData);
    },
  });
  const isCreditScoreActivated =
    !!data?.tuCreditReportIdentityProfile?.vantageScore3;

  const [
    updatePolicyOverrides,
    { loading: isUpdatingPolicyOverrides, error: updatePolicyOverridesError },
  ] = useMutation(UPDATE_INSURANCE_POLICY_OVERRIDES_MUTATION);

  const trackFormUpdates = useCallback(async () => {
    const segmentPayload = {
      policy_id: policyId,
      policy_type: policyType,
      carrier_name: carrierName,
    };

    for (const key in formData) {
      if (formData[key] !== initialFormData[key]) {
        const trackingKey = `is_${snakeCase(key)}_updated`;

        segmentPayload[trackingKey] = true;
      }
    }

    await segmentTrack(
      'NW Plus Insurance Canopy Flow Your Information Screen Save Button Clicked',
      segmentPayload,
    );
  }, [
    policyId,
    policyType,
    carrierName,
    segmentTrack,
    formData,
    initialFormData,
  ]);

  const handleSubmit = useCallback(async () => {
    await trackFormUpdates();

    await updatePolicyOverrides(
      pickBy(
        {
          variables: {
            policyId,
            localOverrides: {
              carYear: parseInt(formData.carYear, 10),
              carMake: formData.carMake,
              carModel: formData.carModel,
              zipCode: formData.zipCode,
              yearBuilt: parseInt(formData.yearBuilt, 10),
            },
            globalOverrides: {
              gender: formData.gender,
              birthdate: parseBirthdate(formData.birthdate),
              creditScore: parseInt(formData.creditScore, 10),
            },
          },
        },
        (value) => !!value,
      ),
    );

    apolloClient.refetchQueries({
      updateCache(cache) {
        cache.evict({ fieldName: 'insurancePolicyRateReport' });
      },
    });
    onSubmit?.();
  }, [
    updatePolicyOverrides,
    trackFormUpdates,
    policyId,
    formData,
    apolloClient,
    onSubmit,
  ]);

  if (loading) {
    return null;
  }

  if (getPolicyOverridesError || updatePolicyOverridesError) {
    return (
      <ErrorBanner errorMessage="Looks like something went wrong. Try again later." />
    );
  }

  return (
    <div>
      <Title size="small">Your information</Title>
      <Box marginTop="3">
        <Text>
          Help us get the most accurate insurance rates for you by keeping this
          information up to date.
        </Text>
      </Box>

      <Form onSubmit={handleSubmit}>
        <Box marginTop={4}>
          <Stack spacing="3-half">
            <PersonalInfoForm isCreditScoreActivated={isCreditScoreActivated} />
            <Switch>
              <Case condition={policyType === InsuranceType.Auto}>
                <AutoInfoForm />
              </Case>
              <Case condition={policyType === InsuranceType.Homeowners}>
                <HomeInfoForm />
              </Case>
              <Case condition={policyType === InsuranceType.Renters}>
                <RentersInfoForm />
              </Case>
            </Switch>
            <Button
              type="submit"
              loading={isUpdatingPolicyOverrides}
              disabled={hasValidationErrors || isUpdatingPolicyOverrides}
              primary
              block
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Form>
    </div>
  );
};

export default ReviewInformationForm;
